import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { Link, useHistory } from 'react-router-dom';
import { format, isToday, parseISO } from 'date-fns';
import { toast } from 'react-toastify';
import {
  FaTimes,
  FaEye,
  FaEraser,
  FaChevronLeft,
  FaChevronRight,
  FaSearch,
  FaEdit,
  FaTrash,
  FaBarcode,
  // FaLocationArrow,
} from 'react-icons/fa';

import Loading from '~/components/Loading';

import { SendFeedback } from '~/components/SendFeedback';

import { useAuth } from '~/hooks';

import api from '~/services/api';

import { Select } from '~/components/Form';
import { TableLoading } from '~/components/Table';
import Pagination from '~/components/Pagination';

import {
  Container,
  Header,
  Controls,
  Table,
  DetailsContainer,
} from '~/styles/components';
import ConfirmWindow from '~/components/ConfirmWindow';
import { confirmAlert } from 'react-confirm-alert';
import formatValue from '~/util/formatValue';
import { formatValueFloat } from '~/util/formatValueFloat';
import {
  FaCalendarPlus,
  FaCloudArrowUp,
  FaDownload,
  FaPlus,
  FaRotateRight,
  FaSort,
  FaSortDown,
  FaSortUp,
} from 'react-icons/fa6';
import base64ToBlob from '~/util/converterBase64ToBLob';
import * as Yup from 'yup';
import fileDownload from 'js-file-download';
import TextareaAutosize from 'react-autosize-textarea';
import { Filter, Content, BilletInfo, Feedback, FeedbackItem } from './styles';

const List = () => {
  const { company, companyUser, user } = useAuth();
  const history = useHistory();

  const filterRef = useRef(null);
  const feedbackRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [deleteFeedbackLoading, setDeleteFeedbackLoading] = useState(false);
  const [uploadFile, setUploadFile] = useState([]);
  const [copyFile, setCopyFile] = useState(null);

  const [showTable, setShowTable] = useState(true);

  const [billets, setBillets] = useState([]);
  const [billet, setBillet] = useState({});
  const [billetIndex, setBilletIndex] = useState(0);
  const [totalBillets, setTotalBillets] = useState(0);

  const [selectedIds, setSelectedIds] = useState([]);

  const [currentPage, setCurrentPage] = useState(() => {
    const currentPageStorage = localStorage.getItem('@Diretiva:billet:page');

    if (currentPageStorage) {
      return Number(currentPageStorage);
    }

    return 1;
  });
  const [totalPages, setTotalPages] = useState(1);

  const [selectedClient, setSelectedClient] = useState(() => {
    const clientStorage = localStorage.getItem(
      '@Diretiva:billet:filter:drawee'
    );

    if (clientStorage) {
      return JSON.parse(clientStorage);
    }

    return { value: '', label: 'Todos' };
  });

  const [selectedPayStatus, setSelectedPayStatus] = useState(() => {
    const payStatusStorage = localStorage.getItem(
      '@Diretiva:billet:filter:pay_status'
    );

    if (payStatusStorage) {
      return JSON.parse(payStatusStorage);
    }

    return { value: '', label: 'Todos' };
  });

  const [selectedType, setSelectedType] = useState(() => {
    const typeStorage = localStorage.getItem('@Diretiva:billet:filter:type');

    if (typeStorage) {
      return JSON.parse(typeStorage);
    }

    return { value: '', label: 'Todos' };
  });

  const [selectedSituation, setSelectedSituation] = useState(() => {
    const situationStorage = localStorage.getItem(
      '@Diretiva:billet:filter:situation'
    );

    if (situationStorage) {
      return JSON.parse(situationStorage);
    }

    return {
      value: 'Aguardando envio para o banco',
      label: 'Aguardando envio para o banco',
    };
  });

  const [sortConfig, setSortConfig] = useState({
    key: 'expiration_date',
    direction: 'desc',
  });

  const sort = useCallback(
    key => {
      let direction = 'asc';
      if (
        sortConfig &&
        sortConfig.key === key &&
        sortConfig.direction === 'asc'
      ) {
        direction = 'desc';
      }
      setSortConfig({ key, direction });
    },
    [sortConfig]
  );

  const loadBillets = useCallback(
    async transmittedIds => {
      if (company) {
        try {
          setShowTable(true);
          setLoading(true);

          const response = await api.get('/billet', {
            params: {
              company_id: company.old_id,
              page: currentPage,
              key: sortConfig.key,
              direction: sortConfig.direction,
              drawee: selectedClient.value,
              pay_status: selectedPayStatus.value,
              situation_text: transmittedIds ? '' : selectedSituation.value,
              transmittedIds,
              type: selectedType.value,
            },
          });

          if (response.data.docs.length > 0) {
            const formattedBillets = response.data.docs.map(item => ({
              ...item,
              price: formatValue(item.price),
              payed_value: formatValue(item.payed_value),
              amount_credited: formatValue(item.amount_credited),
              interest: formatValueFloat(item.interest),
              fine: formatValueFloat(item.fine),
              expiration_date: item.expiration_date
                ? format(parseISO(item.expiration_date), 'dd/MM/yyyy')
                : '',
              emition_date: item.emition_date
                ? format(parseISO(item.emition_date), 'dd/MM/yyyy')
                : '',
              last_receipt: item.last_receipt
                ? format(parseISO(item.last_receipt), 'dd/MM/yyyy')
                : '',
              situation_text: item.billet_situations?.description || '',
              billet_finance: (item.billet_finance || []).map(
                billet_finance => ({
                  ...billet_finance,
                  price: formatValue(billet_finance.price),
                })
              ),
              feedback: (item.billet_feedback || []).map(feedbackItem => ({
                ...feedbackItem,
                feedback_date_formatted: feedbackItem.feedback_date
                  ? format(
                      new Date(feedbackItem.feedback_date),
                      "dd/MM/yyyy 'às' HH:mm"
                    )
                  : null,
              })),
            }));

            setBillets(formattedBillets);
            setTotalPages(response.data.pages);
            setTotalBillets(response.data.total);
            setBillet(formattedBillets[0]);
          } else {
            toast.warn('Nenhum boleto foi encontrado.', {
              position: toast.POSITION.BOTTOM_RIGHT,
            });
          }
        } catch (error) {
          toast.error(
            `Falha ao buscar boletos: ${JSON.stringify(
              error.response?.data?.error
            ) ||
              error.message ||
              'Erro desconhecido'}`,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
            }
          );
        } finally {
          setLoading(false);
        }
      }
    },
    [
      company,
      selectedClient,
      selectedPayStatus,
      selectedSituation,
      sortConfig,
      currentPage,
      selectedType,
    ]
  );

  const [situationOptions, setSitationOptions] = useState([]);

  const loadSituations = useCallback(async () => {
    try {
      const response = await api.get('/billet/billet-situations');

      let options = [];
      if (response.data.length > 0) {
        options = response.data
          .map(item => ({
            value: item.description,
            label: item.description,
          }))
          .filter(
            option => option.value !== undefined && option.value !== null
          );
      } else {
        toast.warn('Nenhuma situação de boleto foi encontrada.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      }

      options.unshift(
        { value: '', label: 'Todos' },
        { value: '-9999', label: 'Excluídos' }
      );
      setSitationOptions(options);
    } catch {
      toast.error('Falha ao buscar situações de boleto.', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  }, []);

  useEffect(() => {
    loadSituations();
  }, [loadSituations]);

  const [clientsOptions, setClientsOptions] = useState([]);

  const loadClients = useCallback(async () => {
    if (company) {
      try {
        const response = await api.get(`/billet/clients`, {
          params: {
            company_id: company.id,
          },
        });

        let options = [];
        if (response.data.length > 0) {
          response.data.sort((a, b) => {
            if (a.name.toUpperCase() < b.name.toUpperCase()) {
              return -1;
            }
            if (a.name.toUpperCase() > b.name.toUpperCase()) {
              return 1;
            }
            return 0;
          });

          options = response.data
            .map(item => ({
              value: item.old_id,
              label: item.name,
            }))
            .filter(
              option => option.value !== undefined && option.value !== null
            );
        } else {
          toast.warn('Nenhum cliente foi encontrado.', {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        }

        options.unshift({ value: '', label: 'Todos' });
        setClientsOptions(options);
      } catch (error) {
        toast.error(
          `Falha ao buscar clientes: ${JSON.stringify(
            error.response?.data?.error
          ) ||
            error.message ||
            'Erro desconhecido'}`,
          {
            position: toast.POSITION.BOTTOM_RIGHT,
          }
        );
      }
    }
  }, [company]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const payStatusOptions = useMemo(() => {
    return [
      { value: '', label: 'Todos' },
      { value: 0, label: 'Em aberto' },
      { value: 1, label: 'Liquidados' },
    ];
  }, []);

  const typeOptions = useMemo(() => {
    return [
      { value: '', label: 'Todos' },
      { value: 1, label: 'Avulso' },
      { value: 0, label: 'Recorrente' },
    ];
  }, []);

  useEffect(() => {
    loadBillets();
  }, [loadBillets]);

  const handleIdsChange = id => {
    setSelectedIds(prevIds =>
      prevIds.includes(id)
        ? prevIds.filter(itemIds => itemIds !== id)
        : [...prevIds, id]
    );
  };

  const handleEdit = useCallback(() => {
    if (companyUser.level > 0) history.push(`/billet/edit/${billet.id}`);
  }, [history, billet, companyUser]);

  const handleDelete = useCallback(async () => {
    if (companyUser.level > 0) {
      try {
        await api.delete(`/billet/${billet.id}`);
        toast.success('Boleto excluído com sucesso.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        loadBillets();
      } catch {
        toast.error('Falha ao excluir boleto.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      }
    }
  }, [companyUser, billet, loadBillets]);

  const confirmDeleteForm = useCallback(() => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <ConfirmWindow
            onClick={handleDelete}
            onClose={onClose}
            message="Todos os dados serão Deletados com esta ação."
          />
        );
      },
      closeOnEscape: false,
      closeOnClickOutside: false,
    });
  }, [handleDelete]);

  const handleView = useCallback(() => {
    if (billets.length > 0) {
      setShowTable(!showTable);
    }
  }, [showTable, billets]);

  const handlePrevItem = useCallback(() => {
    if (billetIndex !== 0) {
      setBilletIndex(billetIndex - 1);
      setBillet(billets[billetIndex - 1]);
    } else {
      setBilletIndex(billets.length - 1);
      setBillet(billets[billets.length - 1]);
    }
  }, [billetIndex, billets]);

  const handleNextItem = useCallback(() => {
    if (billetIndex !== billets.length - 1) {
      setBilletIndex(billetIndex + 1);
      setBillet(billets[billetIndex + 1]);
    } else {
      setBilletIndex(0);
      setBillet(billets[0]);
    }
  }, [billetIndex, billets]);

  const handleFilter = useCallback(
    ({ drawee, pay_status, situation, type }) => {
      setCurrentPage(1);
      localStorage.setItem('@Diretiva:billet:page', 1);

      setSelectedPayStatus(
        payStatusOptions.find(option => option.value === pay_status)
      );
      localStorage.setItem(
        '@Diretiva:billet:filter:pay_status',
        JSON.stringify(
          payStatusOptions.find(option => option.value === pay_status)
        )
      );

      setSelectedType(typeOptions.find(option => option.value === type));
      localStorage.setItem(
        '@Diretiva:billet:filter:type',
        JSON.stringify(typeOptions.find(option => option.value === type))
      );

      setSelectedSituation(
        situationOptions.find(option => option.value === situation)
      );
      localStorage.setItem(
        '@Diretiva:billet:filter:situation',
        JSON.stringify(
          situationOptions.find(option => option.value === situation)
        )
      );

      setSelectedClient(clientsOptions.find(option => option.value === drawee));
      localStorage.setItem(
        '@Diretiva:billet:filter:drawee',
        JSON.stringify(clientsOptions.find(option => option.value === drawee))
      );
    },
    [clientsOptions, payStatusOptions, situationOptions, typeOptions]
  );

  const handleResetFilter = useCallback(() => {
    setCurrentPage(1);
    localStorage.removeItem('@Diretiva:billet:page');

    filterRef.current.reset();

    filterRef.current.setFieldValue('pay_status', {
      value: '',
      label: 'Todos',
    });
    setSelectedPayStatus({
      value: '',
      label: 'Todos',
    });
    localStorage.removeItem('@Diretiva:billet:filter:pay_status');

    filterRef.current.setFieldValue('type', {
      value: '',
      label: 'Todos',
    });
    setSelectedType({
      value: '',
      label: 'Todos',
    });
    localStorage.removeItem('@Diretiva:billet:filter:type');

    filterRef.current.setFieldValue('drawee', {
      value: '',
      label: 'Todos',
    });
    setSelectedClient({
      value: '',
      label: 'Todos',
    });
    localStorage.removeItem('@Diretiva:billet:filter:drawee');

    filterRef.current.setFieldValue('situation', {
      value: 'Aguardando envio para o banco',
      label: 'Aguardando envio para o banco',
    });
    setSelectedSituation({
      value: 'Aguardando envio para o banco',
      label: 'Aguardando envio para o banco',
    });
    localStorage.removeItem('@Diretiva:billet:filter:situation');
  }, []);

  const handleDetails = useCallback(
    index => {
      setBillet(billets[index]);
      setBilletIndex(index);
      handleView();
    },
    [billets, handleView]
  );

  const handlePage = useCallback(
    page => {
      if (page === 0) {
        setCurrentPage(1);
        sessionStorage.setItem('@Diretiva:billet:page', 1);
      } else if (page > totalPages) {
        setCurrentPage(totalPages);
        sessionStorage.setItem('@Diretiva:billet:page', totalPages);
      } else {
        setCurrentPage(page);
        sessionStorage.setItem('@Diretiva:billet:page', page);
      }
    },
    [totalPages]
  );

  const handleTransmitPayments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.post(
        '/billet/transmit-payment',
        { data: selectedIds },
        {
          params: {
            level: companyUser.level,
            user_id: user.id,
          },
        }
      );

      toast.success('Boleto(s) transmitido(s) com sucesso.', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });

      if (response.data.transmitted > 0) {
        const transmittedIds = selectedIds.join(', ');
        loadBillets(transmittedIds);
      }
    } catch (error) {
      toast.error(
        `Nao foi possivel transmitir o(s) boleto(s): ${JSON.stringify(
          error.response?.data?.error
        ) ||
          error.message ||
          'Erro desconhecido'}`,
        {
          position: toast.POSITION.BOTTOM_RIGHT,
        }
      );
    } finally {
      setSelectedIds([]);
      setLoading(false);
    }
  }, [companyUser, loadBillets, selectedIds, user]);

  const confirmTransmitPayments = useCallback(() => {
    if (selectedIds.length === 0) {
      toast.warn('Nenhum boleto foi selecionado para transmissão.', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return;
    }
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <ConfirmWindow
            onClick={handleTransmitPayments}
            onClose={onClose}
            message="Todos os boletos selecionados serão transmitidos."
          />
        );
      },
      closeOnEscape: false,
      closeOnClickOutside: false,
    });
  }, [handleTransmitPayments, selectedIds]);

  const handleReturnPayments = useCallback(async () => {
    try {
      setLoading(true);
      await api.post(`/billet/return-payment/${billet.id}`, null, {
        params: {
          user_id: user.id,
        },
      });

      toast.success('Retorno(s) obtido(s) com sucesso.', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });

      const response = await api.get(`/billet/${billet.id}`, {
        params: {
          company_id: company.old_id,
        },
      });
      const billetData = response.data;
      const billetFormatted = {
        ...billetData,
        price: formatValue(billetData.price),
        payed_value: formatValue(billetData.payed_value),
        amount_credited: formatValue(billetData.amount_credited),
        interest: formatValueFloat(billetData.interest),
        fine: formatValueFloat(billetData.fine),
        expiration_date: billetData.expiration_date
          ? format(parseISO(billetData.expiration_date), 'dd/MM/yyyy')
          : '',
        emition_date: billetData.emition_date
          ? format(parseISO(billetData.emition_date), 'dd/MM/yyyy')
          : '',
        last_receipt: billetData.last_receipt
          ? format(parseISO(billetData.last_receipt), 'dd/MM/yyyy')
          : '',
        situation_text: billetData.billet_situations?.description || '',
        billet_finance: (billetData.billet_finance || []).map(item => ({
          ...item,
          price: formatValue(item.price),
        })),
        feedback: (billetData.billet_feedback || []).map(feedbackItem => ({
          ...feedbackItem,
          feedback_date_formatted: feedbackItem.feedback_date
            ? format(
                new Date(feedbackItem.feedback_date),
                "dd/MM/yyyy 'às' HH:mm"
              )
            : null,
        })),
      };

      setBillets(
        billets.map(billetItem =>
          billetItem.id === billetFormatted.id ? billetFormatted : billetItem
        )
      );

      setBillet(billetFormatted);
    } catch (error) {
      toast.error(
        `Nao foi possível obter o(s) retorno(s): ${JSON.stringify(
          error.response?.data?.error
        ) ||
          error.message ||
          'Erro desconhecido'}`,
        {
          position: toast.POSITION.BOTTOM_RIGHT,
        }
      );
    } finally {
      setLoading(false);
    }
  }, [billet, billets, company, user]);

  const confirmReturnPayments = useCallback(() => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <ConfirmWindow
            onClick={handleReturnPayments}
            onClose={onClose}
            message="Será efetuada a busca de retorno nos boletos selecionados."
          />
        );
      },
      closeOnEscape: false,
      closeOnClickOutside: false,
    });
  }, [handleReturnPayments]);

  const handlePostpone = useCallback(async () => {
    try {
      setLoading(true);
      if (billet.type !== 1) {
        toast.warn('Só é permitido adiar boletos avulsos.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        return;
      }

      await api.post(`/billet/postpone/${billet.id}`);

      toast.success('Boleto adiado com sucesso.', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } catch (error) {
      toast.error(
        `Não foi possível adiar o boleto: ${JSON.stringify(
          error.response?.data?.error
        ) ||
          error.message ||
          'Erro desconhecido'}`,
        {
          position: toast.POSITION.BOTTOM_RIGHT,
        }
      );
    } finally {
      setLoading(false);
    }
  }, [billet]);

  const handleFeedback = async (data, feedbackReport) => {
    if (company && user) {
      let blob = null;
      if (copyFile) {
        blob = base64ToBlob(copyFile);
      }
      try {
        setFeedbackLoading(true);

        if (copyFile) {
          data.file = {};
          data.file.name = 'feedback.png';
          data.file.type = 'image/png';
        }

        const schema = Yup.object().shape({
          feedback_message: Yup.string().when('file', {
            is: value => value !== undefined,
            then: Yup.string(),
            otherwise: Yup.string().required('A mensagem é obrigatória'),
          }),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        const feedbackData = {
          company_id: company.id,
          billet_id: billet.id,
          user_id: user.id,
          feedback: feedbackReport,
          content:
            data.file && data.feedback_message === ''
              ? data.file.name
              : data.feedback_message,
          type: data.file ? 1 : 0,
          file_name: data.file ? data.file.name : null,
        };

        if (data.file) {
          const formData = new FormData();

          if (!copyFile) {
            formData.append('file', data.file);
          } else {
            formData.append('file', blob, 'feedback.png');
            setCopyFile(null);
          }

          const fileResponse = await api.post('files/upload', formData, {
            params: {
              prefix: 'Billet_Feedback',
            },
          });

          const { blobName } = fileResponse.data;

          feedbackData.link = blobName;
        }

        await api.post('billet/feedback', feedbackData);

        const response = await api.get(`/billet/${billet.id}`, {
          params: {
            company_id: company.old_id,
          },
        });

        const billetData = response.data;

        const billetFormatted = {
          ...billetData,
          price: formatValue(billetData.price),
          payed_value: formatValue(billetData.payed_value),
          amount_credited: formatValue(billetData.amount_credited),
          interest: formatValueFloat(billetData.interest),
          fine: formatValueFloat(billetData.fine),
          expiration_date: billetData.expiration_date
            ? format(parseISO(billetData.expiration_date), 'dd/MM/yyyy')
            : '',
          emition_date: billetData.emition_date
            ? format(parseISO(billetData.emition_date), 'dd/MM/yyyy')
            : '',
          last_receipt: billetData.last_receipt
            ? format(parseISO(billetData.last_receipt), 'dd/MM/yyyy')
            : '',
          billet_finance: (billetData.billet_finance || []).map(item => ({
            ...item,
            price: formatValue(item.price),
          })),
          feedback: (billetData.billet_feedback || []).map(feedbackItem => ({
            ...feedbackItem,
            feedback_date_formatted: feedbackItem.feedback_date
              ? format(
                  new Date(feedbackItem.feedback_date),
                  "dd/MM/yyyy 'às' HH:mm"
                )
              : null,
          })),
        };

        setBillets(
          billets.map(billetItem =>
            billetItem.id === billetFormatted.id ? billetFormatted : billetItem
          )
        );

        setBillet(billetFormatted);

        feedbackRef.current.clearField('feedback_message');

        feedbackRef.current.setErrors({});

        setUploadFile([]);
        setFeedbackLoading(false);

        toast.success('Feedback salvo com sucesso.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errorMessages = {};

          error.inner.forEach(errr => {
            errorMessages[errr.path] = errr.message;
          });

          feedbackRef.current.setErrors(errorMessages);
        } else {
          toast.error(
            `Falha ao salvar feedback: ${JSON.stringify(
              error.response?.data?.error
            ) ||
              error.message ||
              'Erro desconhecido'}`,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
            }
          );
        }
        setUploadFile([]);
        setFeedbackLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!showTable && !loading) {
      const element = document.getElementById('feedback_message');

      if (element) {
        element.addEventListener('keydown', event => {
          if (event.ctrlKey && event.key === 'Enter') {
            feedbackRef.current.submitForm();
          }
        });
      }
    }
  }, [showTable, loading]);

  useEffect(() => {
    if (uploadFile.length !== 0) {
      feedbackRef.current.submitForm();
    }
  }, [uploadFile]);

  const openFile = useCallback(async blobName => {
    const response = await api.get('files/download', {
      params: {
        blobName,
      },
      responseType: 'blob',
    });

    const fileURL = URL.createObjectURL(response.data);

    window.open(fileURL, '_blank');
  }, []);

  const downloadFile = useCallback(async (blobName, fileName) => {
    const response = await api.get('files/download', {
      params: {
        blobName,
      },
      responseType: 'blob',
    });

    fileDownload(response.data, fileName);
  }, []);

  const handleDeleteFeedback = useCallback(
    async (id, billetId, userId, date) => {
      if (user) {
        if (user.id === userId) {
          if (isToday(parseISO(date))) {
            setDeleteFeedbackLoading(true);
            try {
              await api.delete(`billet/feedback/${id}`);

              setBillets(
                billets.map(item =>
                  item.id === billetId
                    ? {
                        ...item,
                        feedback: item?.feedback?.filter(
                          feedback => feedback.id !== id
                        ),
                      }
                    : item
                )
              );

              setBillet(oldBillet => ({
                ...oldBillet,
                feedback: oldBillet?.feedback?.filter(
                  feedback => feedback.id !== id
                ),
              }));

              toast.success('Mensagem deletada com sucesso.', {
                position: toast.POSITION.BOTTOM_RIGHT,
              });
            } catch {
              toast.error('Falha ao deletar mensagem.', {
                position: toast.POSITION.BOTTOM_RIGHT,
              });
            } finally {
              setDeleteFeedbackLoading(false);
            }
          } else {
            toast.warn('Não é possivel deletar mensagens antigas.', {
              position: toast.POSITION.BOTTOM_RIGHT,
            });
          }
        } else {
          toast.warn('Somente o criador da mensagem pode deletá-lo.', {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        }
      }
    },
    [user, billets]
  );

  const confirmRemoveFeedback = useCallback(
    (id, billetId, userId, date) => {
      confirmAlert({
        customUI: ({ onClose }) => {
          return (
            <ConfirmWindow
              onClick={() => handleDeleteFeedback(id, billetId, userId, date)}
              onClose={onClose}
            />
          );
        },
        closeOnEscape: false,
        closeOnClickOutside: false,
      });
    },
    [handleDeleteFeedback]
  );

  return (
    <>
      <Container>
        <Header>
          <div>
            <FaBarcode />
            <h1>Cobrança - Boletos</h1>
          </div>

          <Link to="/">
            <FaTimes />
          </Link>
        </Header>
        <Controls>
          <button type="button" onClick={handleView}>
            <FaEye />
            <span>Visualização</span>
          </button>
          <Link to={{ pathname: '/billet/new', state: { id: null } }}>
            <FaPlus />
            <span>Novo</span>
          </Link>
          {showTable ? (
            <>
              <button type="button" onClick={handleResetFilter}>
                <FaEraser />
                <span>Limpar filtros</span>
              </button>
              <button
                className="id"
                type="button"
                onClick={confirmTransmitPayments}
              >
                <FaCloudArrowUp />
                <span>Transmitir boleto(s)</span>
              </button>
            </>
          ) : (
            <div>
              <button type="button" onClick={handleEdit}>
                <FaEdit size={15} color="#44546a" />
                <span>Editar</span>
              </button>
              <button type="button" onClick={confirmDeleteForm}>
                <FaTrash size={15} color="#44546a" />
                <span>Excluir</span>
              </button>
              <button type="button" onClick={handlePostpone}>
                <FaCalendarPlus size={15} color="#44546a" />
                <span>Adiar</span>
              </button>

              <button type="button" onClick={confirmReturnPayments}>
                <FaRotateRight />
                <span>Retorno bancário</span>
              </button>

              <button type="button" onClick={handlePrevItem}>
                <FaChevronLeft />
              </button>
              {totalBillets > 25 ? (
                <span>{billetIndex + 1} de 25</span>
              ) : (
                <span>
                  {billetIndex + 1} de {totalBillets}
                </span>
              )}
              <button type="button" onClick={handleNextItem}>
                <FaChevronRight />
              </button>
            </div>
          )}
        </Controls>
        {showTable && (
          <Filter ref={filterRef} onSubmit={handleFilter}>
            <Select
              label="Cliente"
              name="drawee"
              className="drawee"
              options={clientsOptions}
              defaultValue={selectedClient}
            />
            <Select
              label="Tipo"
              name="type"
              className="type"
              options={typeOptions}
              defaultValue={selectedType}
            />
            <Select
              label="Pgto"
              name="pay_status"
              className="pay_status"
              options={payStatusOptions}
              defaultValue={selectedPayStatus}
            />
            <Select
              label="Situação"
              name="situation"
              className="situation"
              options={situationOptions}
              defaultValue={selectedSituation}
            />

            <button type="submit">
              <FaSearch />
            </button>
          </Filter>
        )}
        {loading ? (
          <TableLoading />
        ) : (
          <Content className="content">
            {showTable ? (
              <Table className="table">
                <thead>
                  <tr>
                    <th className="id" />
                    <th
                      className="expiration_date sort"
                      onClick={() => sort('expiration_date')}
                    >
                      <div>
                        Vencimento
                        {sortConfig.key !== 'expiration_date' && <FaSort />}
                        {sortConfig.key === 'expiration_date' &&
                          sortConfig.direction === 'asc' && <FaSortUp />}
                        {sortConfig.key === 'expiration_date' &&
                          sortConfig.direction === 'desc' && <FaSortDown />}
                      </div>
                    </th>
                    <th className="financial_data">Dados Financeiros</th>
                    <th className="title_number">Número do Título</th>
                    <th className="drawee sort" onClick={() => sort('drawee')}>
                      <div>
                        Cliente (Sacado)
                        {sortConfig.key !== 'drawee' && <FaSort />}
                        {sortConfig.key === 'drawee' &&
                          sortConfig.direction === 'asc' && <FaSortUp />}
                        {sortConfig.key === 'drawee' &&
                          sortConfig.direction === 'desc' && <FaSortDown />}
                      </div>
                    </th>
                    <th className="price_list">Preço</th>
                    <th className="situation">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {billets?.map((item, index) => (
                    <tr
                      key={item.id}
                      className={`hover ${item.status ? 'red-row' : ''}`}
                      onClick={() => handleDetails(index)}
                    >
                      <td className="id checkbox">
                        <input
                          name="id"
                          type="checkbox"
                          checked={selectedIds.includes(item.id)}
                          onChange={() => handleIdsChange(item.id)}
                          onClick={e => e.stopPropagation()}
                        />
                      </td>
                      <td className="expiration_date">
                        {item.expiration_date}
                      </td>
                      <td className="financial_data">
                        {item.financial_data?.title || ''}
                      </td>
                      <td className="title_number">{item.title_number}</td>
                      <td className="drawee">{item.client?.name || ''}</td>
                      <td className="price_list">{item.price}</td>
                      <td className="situation">
                        {item.billet_situations?.description}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <DetailsContainer>
                <BilletInfo>
                  <h4>Boleto</h4>
                  <section>
                    <div className="drawee">
                      <label>Cliente (Sacado)</label>
                      <input
                        name="drawee"
                        value={billet?.client?.name || ''}
                        readOnly
                      />
                    </div>
                    <div className="price">
                      <label>Preço</label>
                      <input
                        name="price"
                        value={billet?.price || ''}
                        readOnly
                      />
                    </div>
                    <div className="emition_date">
                      <label>Emissão</label>
                      <input
                        name="emition_date"
                        value={billet?.emition_date || ''}
                        readOnly
                      />
                    </div>
                    <div className="financial_data">
                      <label>Dados financeiros</label>
                      <input
                        name="financial_data"
                        value={billet?.financial_data?.title || ''}
                        readOnly
                      />
                    </div>
                  </section>
                  <section>
                    <div className="title_number">
                      <label>Número do título</label>
                      <input
                        name="title_number"
                        value={billet?.title_number || ''}
                        readOnly
                      />
                    </div>
                    <div className="expiration_type">
                      <label>Tipo de vencimento</label>
                      <input
                        name="expiration_type"
                        value={billet?.expiration_type_label || ''}
                        readOnly
                      />
                    </div>
                    <div className="expiration_date">
                      <label>Vencimento</label>
                      <input
                        name="expiration_date"
                        value={billet?.expiration_date || ''}
                        readOnly
                      />
                    </div>
                  </section>
                  <section>
                    <div className="interest">
                      <label>Juros</label>
                      <input
                        name="interest"
                        value={billet?.interest || ''}
                        readOnly
                      />
                    </div>
                    <div className="interest_type">
                      <label>Juros (Tipo)</label>
                      <input
                        name="interest_type"
                        value={billet?.interest_type_label || ''}
                        readOnly
                      />
                    </div>
                    <div className="fine">
                      <label>Multa</label>
                      <input name="fine" value={billet?.fine || ''} readOnly />
                    </div>
                    <div className="fine_type">
                      <label>Multa (Tipo)</label>
                      <input
                        name="fine_type"
                        value={billet?.fine_type_label || ''}
                        readOnly
                      />
                    </div>
                    <div className="days_to_fine">
                      <label>Dias para multar</label>
                      <input
                        name="days_to_fine"
                        value={billet?.days_to_fine || ''}
                        readOnly
                      />
                    </div>
                    <div className="protest">
                      <label>Protestar</label>
                      <input
                        name="protest"
                        value={billet?.protest_label || ''}
                        readOnly
                      />
                    </div>
                    <div className="days_to_protest">
                      <label>Dias para protestar</label>
                      <input
                        name="days_to_protest"
                        value={
                          billet?.as_days_to_protest?.days_label || 'Nenhum'
                        }
                        readOnly
                      />
                    </div>
                  </section>
                  <section>
                    <div className="message">
                      <label>Mensagem</label>
                      <input
                        name="message"
                        value={billet?.message || ''}
                        readOnly
                      />
                    </div>
                  </section>
                  <section>
                    <div className="obs">
                      <label>Observação</label>
                      <input name="obs" value={billet?.obs || ''} readOnly />
                    </div>
                  </section>
                  <section>
                    <div className="billet_url">
                      <label>Link</label>
                      <input
                        name="billet_url"
                        value={billet?.billet_url || ''}
                        readOnly
                      />
                    </div>
                  </section>

                  <h4 className="finance_header">Eventos</h4>
                  {(billet.billet_finance || []).length > 0 &&
                    (billet.billet_finance || []).map(billet_finance => (
                      <section key={billet_finance.id}>
                        <div className="finance_name_label">
                          <label>Evento</label>
                          <input
                            name="finance_name_label"
                            value={billet_finance.event?.name_label || ''}
                            readOnly
                          />
                        </div>
                        <div className="finance_competence">
                          <label>Competência</label>
                          <input
                            name="finance_competence"
                            value={billet_finance.name || ''}
                            readOnly
                          />
                        </div>
                        <div className="finance_price">
                          <label>Valor</label>
                          <input
                            name="finance_price"
                            value={billet_finance.price || ''}
                            readOnly
                          />
                        </div>
                      </section>
                    ))}

                  <h4 className="situation_header">Situação</h4>
                  <section>
                    <div className="situation_text">
                      <label>Situação</label>
                      <input
                        name="situation_text"
                        value={billet?.situation_text || ''}
                        readOnly
                      />
                    </div>
                    <div className="last_receipt">
                      <label>Data da situação</label>
                      <input
                        name="last_receipt"
                        value={billet?.last_receipt || ''}
                        readOnly
                      />
                    </div>
                    <div className="payed_value">
                      <label>Valor pago</label>
                      <input
                        name="payed_value"
                        value={billet?.payed_value || ''}
                        readOnly
                      />
                    </div>
                    <div className="amount_credited">
                      <label>Valor creditado</label>
                      <input
                        name="amount_credited"
                        value={billet?.amount_credited || ''}
                        readOnly
                      />
                    </div>
                  </section>
                </BilletInfo>
                <Feedback
                  ref={feedbackRef}
                  onSubmit={async data => handleFeedback(data, true)}
                >
                  <h4>FEEDBACK</h4>
                  <SendFeedback
                    uploadFile={uploadFile}
                    feedbackRef={feedbackRef}
                    copyFile={copyFile}
                    setCopyFile={setCopyFile}
                    setUploadFile={setUploadFile}
                  />
                  {(billet.feedback || []).length > 0 &&
                    (billet.feedback || []).map(feedback => (
                      <FeedbackItem key={feedback.id}>
                        <div className="delete">
                          <button
                            type="button"
                            onClick={() =>
                              confirmRemoveFeedback(
                                feedback.id,
                                feedback.billet_id,
                                feedback.user.id,
                                feedback.feedback_date
                              )
                            }
                          >
                            <FaTrash size={14} />
                          </button>
                        </div>
                        <div className="date">
                          <label>Data/Hora</label>
                          <input
                            name="feedback_date_formatted"
                            value={feedback.feedback_date_formatted}
                            readOnly
                          />
                        </div>
                        <div className="user">
                          <label>Usuário</label>
                          <input
                            name="feedback_user"
                            value={feedback.user.short_name}
                            readOnly
                          />
                        </div>
                        <div className="content">
                          <label>Mensagem</label>
                          <TextareaAutosize
                            name="content"
                            value={feedback.content}
                            readOnly
                            maxRows={4}
                          />
                        </div>
                        {feedback.type === 1 && (
                          <div className="file">
                            <aside>
                              <button
                                type="button"
                                onClick={() => openFile(feedback.link)}
                                title="Visualizar arquivo"
                              >
                                <FaEye size={16} />
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  downloadFile(
                                    feedback.link,
                                    feedback.file_name
                                  )
                                }
                                title="Baixar arquivo"
                              >
                                <FaDownload size={16} />
                              </button>
                            </aside>
                            <div>
                              <label>Arquivo</label>
                              <input
                                type="text"
                                value={feedback.file_name}
                                readOnly
                              />
                            </div>
                          </div>
                        )}
                      </FeedbackItem>
                    ))}
                </Feedback>
              </DetailsContainer>
            )}
          </Content>
        )}
        <Pagination
          loading={loading ? 1 : 0}
          currentPage={currentPage}
          pages={totalPages}
          totalDocs={totalBillets}
          handlePage={handlePage}
        />
      </Container>
      {(loading || feedbackLoading || deleteFeedbackLoading) && <Loading />}
    </>
  );
};

export default List;
